import { useEffect, useMemo } from "react"
import styled from "styled-components"

import {
  useGetEventTypesQuery,
  useGetNotificationsQuery,
  useMarkAllAsReadMutation,
  useMarkAsReadMutation,
  useMarkAsUnreadMutation,
} from "store/api/notificationApi"
import { ApiTypes, UITypes } from "types"

import useUrlQueryParams from "hooks/useUrlQueryParams"
import { ContentWrapper } from "components/common/ContentWrapper"
import { NotificationCard } from "components/common/NotificationCard"
import { Pagination } from "components/common/Pagination"
import { Spinner } from "components/common/Spinner"

import { NotificationsEmptyState } from "./NotificationsEmptyState"
import { NotificationsFilters } from "./NotificationsFilters"
import { NotificationsHeader } from "./NotificationsHeader"

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-bottom: 24px;
  max-width: 860px;
  width: 100%;
  margin: 0 auto;
  min-height: calc(100vh - 148px);
`

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

const NotificationsList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`

const LoadingWrapper = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  min-height: 200px;
`

interface RenderNotificationsListProps {
  isLoading: boolean
  isFetching: boolean
  notificationsData: ApiTypes.Res.Notifications | undefined
  hasActiveFilters: boolean
  onToggleRead: (id: number, isRead: boolean) => Promise<void>
}

const renderNotificationsList = ({
  isLoading,
  isFetching,
  notificationsData,
  hasActiveFilters,
  onToggleRead,
}: RenderNotificationsListProps) => {
  if (isLoading) {
    return (
      <LoadingWrapper>
        <Spinner />
      </LoadingWrapper>
    )
  }

  if (notificationsData?.data && notificationsData.data.length > 0) {
    return (
      <>
        {isFetching && (
          <LoadingWrapper>
            <Spinner />
          </LoadingWrapper>
        )}
        {notificationsData.data.map(notification => (
          <NotificationCard key={notification.id} notification={notification} onToggleRead={onToggleRead} />
        ))}
      </>
    )
  }

  return <NotificationsEmptyState hasFilters={hasActiveFilters} />
}

export const NotificationsPage = () => {
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()

  const queryParams: UITypes.NotificationParams = useMemo(() => {
    const params: UITypes.NotificationParams = {
      page: urlQueryParams["page"] ? parseInt(urlQueryParams["page"] as string) : 1,
      per_page: urlQueryParams["perPage"] ? parseInt(urlQueryParams["perPage"] as string) : 50,
      orderColumn: "created_at",
      orderType: "DESC",
    }

    if (urlQueryParams["isRead"] === "false") {
      params.is_read = false
    }

    if (urlQueryParams["eventTypeId"]) {
      params.event_type_id = parseInt(urlQueryParams["eventTypeId"] as string)
    }

    if (urlQueryParams["rangeDays"]) {
      const rangeDays = parseInt(urlQueryParams["rangeDays"] as string)
      if (rangeDays > 0) {
        params.range_days = rangeDays
      }
    }

    return params
  }, [urlQueryParams])

  const { data: notificationsData, isFetching, isLoading } = useGetNotificationsQuery(queryParams)
  const { data: eventTypesData } = useGetEventTypesQuery()

  const [markAsRead] = useMarkAsReadMutation()
  const [markAsUnread] = useMarkAsUnreadMutation()
  const [markAllAsRead, { isLoading: isMarkingAllAsRead }] = useMarkAllAsReadMutation()

  const eventTypeOptions = useMemo(() => {
    if (!eventTypesData?.data) return []
    return [
      { value: 0, label: "All notifications" },
      ...eventTypesData.data.map(type => ({
        value: type.id,
        label: type.name,
      })),
    ]
  }, [eventTypesData])

  const handleToggleRead = async (id: number, isRead: boolean) => {
    if (isRead) {
      await markAsUnread(id)
    } else {
      await markAsRead(id)
    }
  }

  const handleMarkAllAsRead = async () => {
    await markAllAsRead()
  }

  const hasActiveFilters = useMemo(() => {
    const selectedEventType = urlQueryParams["eventTypeId"] ? parseInt(urlQueryParams["eventTypeId"] as string) : null
    const selectedTimeRange = urlQueryParams["rangeDays"] ? parseInt(urlQueryParams["rangeDays"] as string) : 0
    return selectedEventType !== null || selectedTimeRange > 0
  }, [urlQueryParams])

  useEffect(() => {
    const currentPage = queryParams.page
    if (currentPage !== 1 && notificationsData?.data && notificationsData.data.length === 0) {
      setUrlQueryParams({ ...urlQueryParams, page: "1" })
    }
  }, [notificationsData, queryParams.page, urlQueryParams, setUrlQueryParams])

  return (
    <ContentWrapper>
      <PageContainer>
        <NotificationsHeader />

        <FilterSection>
          <NotificationsFilters
            eventTypeOptions={eventTypeOptions}
            isMarkingAllAsRead={isMarkingAllAsRead}
            isFetching={isFetching}
            onMarkAllAsRead={handleMarkAllAsRead}
          />
          <NotificationsList>
            {renderNotificationsList({
              isLoading,
              isFetching,
              notificationsData,
              hasActiveFilters,
              onToggleRead: handleToggleRead,
            })}
          </NotificationsList>

          {notificationsData?.meta && notificationsData.data && notificationsData.data.length > 0 && (
            <Pagination meta={notificationsData.meta} />
          )}
        </FilterSection>
      </PageContainer>
    </ContentWrapper>
  )
}
